<template>
  <el-table
    v-loading="loading"
    :element-loading-text="getLoadingText"
    border
    :data="tableData"
    :style="tableStyle"
  >
    <el-table-column
      prop="action"
      :label="$t('CampaignAnalytics_JourneyActions_Action')"
    >
      <template slot-scope="scope">
        {{ $t(translationsMap(scope.row.action)) }}
        <span v-if="scope.row.action === 'Delay:waiting'">
          <el-tooltip
            :content="$t('CampaignAnalytics_JourneyActions_WaitingMessage')"
          >
            <i class="ico el-icon-warning-outline tooltip-icon"></i>
          </el-tooltip>
        </span>
      </template>
    </el-table-column>
    <el-table-column
      prop="unique_users"
      :label="$t('CampaignAnalytics_JourneyActions_UniqueUsers')"
    />
    <el-table-column
      prop="total_users"
      :label="$t('CampaignAnalytics_JourneyActions_TotalUsers')"
    />
  </el-table>
</template>

<script>
import { projectEndpoint } from '@/core/helpers'
import RestApi from '@/core/services/RestApi'
import { mxaTaskStatus } from '@/modules/campaign-manage/enum/MxaTaskStatusEnum'

export default {
  name: 'JourneyActions',
  props: {
    model: {
      type: Object,
      require: true
    },
    analyticsDateRange: {
      type: Object,
      required: true
    },
    journeyActionVariant: {
      type: String,
      required: false
    }
  },
  emits: ['loading'],
  created() {
    this.fetchData()
  },
  destroyed() {
    if (this.lastTimeout) {
      clearTimeout(this.lastTimeout)
    }
  },
  data() {
    return {
      tableData: [],
      loading: false,
      workflowStats: [],
      pollStart: 0,
      lastTimeout: null,
      maxTimeout: 5 * 60000
    }
  },

  methods: {
    setLoading(loading) {
      this.loading = loading
      this.$emit('loading', loading)
    },
    // @todo XP-9472 remove this once MXA 2025.4 is rolled out
    fetchDataLegacy() {
      this.setLoading(true)
      RestApi(
        projectEndpoint({
          method: 'get',
          url: `campaigns/${this.$route.query.id}/analytics/journey-actions-data`,
          params: {
            startDate: this.analyticsDateRange.date_from,
            endDate: this.analyticsDateRange.date_to,
            variantKey: this.journeyActionVariant
          }
        })
      )
        .then(({ data }) => {
          this.tableData = data
        })
        .catch(error => {
          this.$message({
            message: error.message,
            type: 'error'
          })
        })
        .finally(() => {
          this.setLoading(false)
        })
    },

    fetchData() {
      if (this.loading) {
        return
      }
      this.setLoading(true)
      RestApi(
        projectEndpoint({
          method: 'get',
          url: `campaigns/${this.$route.query.id}/analytics/journey-actions-start-task`,
          params: {
            startDate: this.analyticsDateRange.date_from,
            endDate: this.analyticsDateRange.date_to,
            variantKey: this.journeyActionVariant
          }
        })
      )
        .then(({ data }) => {
          this.pollStart = Date.now()
          this.pollTask(data.taskId)
        })
        .catch(() => {
          this.fetchDataLegacy()
        })
    },

    pollTask(taskId, backoff = 1000) {
      RestApi(
        projectEndpoint({
          method: 'get',
          url: `campaigns/${this.$route.query.id}/analytics/journey-actions-poll-task`,
          params: {
            taskId: taskId
          }
        })
      )
        .then(({ data }) => {
          switch (data.status) {
            case mxaTaskStatus.READY:
            case mxaTaskStatus.IN_PROGRESS:
              // Timeout if task hasn't finished in 5 minutes
              if (Date.now() - this.pollStart > this.maxTimeout) {
                this.$message({
                  message: this.$t(
                    'CampaignAnalytics_JourneyActions_TimeoutMessage'
                  ),
                  type: 'error'
                })
                this.setLoading(false)
                break
              }
              this.lastTimeout = setTimeout(() => {
                this.pollTask(taskId, backoff + 1000)
              }, backoff)
              break
            case mxaTaskStatus.FAIL:
            case mxaTaskStatus.ERROR:
              this.$message({
                message: data.result.message,
                type: 'error'
              })
              this.setLoading(false)
              break
            case mxaTaskStatus.SUCCESS:
              this.tableData = data.result
              this.setLoading(false)
          }
        })
        .catch(error => {
          this.$message({
            message: error.message,
            type: 'error'
          })
          this.setLoading(false)
        })
    },

    translationsMap(key) {
      const map = {
        'Start:entered': 'CampaignAnalytics_JourneyActions_Entered',
        'Delay:delayed': 'CampaignAnalytics_JourneyActions_Delayed',
        'Delay:cancelled': 'CampaignAnalytics_JourneyActions_Cancelled',
        'Delay:earlyCancel': 'CampaignAnalytics_JourneyActions_Earlycancel',
        'Delay:resumed': 'CampaignAnalytics_JourneyActions_Resumed',
        'Delay:waiting': 'CampaignAnalytics_JourneyActions_Waiting',
        'End:completed': 'CampaignAnalytics_JourneyActions_Completed'
      }

      return map[key]
    }
  },

  computed: {
    getLoadingText() {
      return this.$t('CampaignAnalytics_JourneyActions_Loading_Text')
    },
    tableStyle() {
      return {
        width: '100%',
        minHeight: this.loading ? '350px' : 'auto'
      }
    }
  },

  watch: {
    analyticsDateRange: {
      handler: function() {
        this.fetchData()
      },
      deep: true
    },

    journeyActionVariant: {
      handler: function() {
        this.fetchData()
      }
    }
  }
}
</script>

<style scoped>
>>> .el-loading-text {
  white-space: pre-line !important;
  line-height: 1.6 !important;
  max-width: 400px !important;
  margin: 0 auto !important;
  padding: 0 20px !important;
  text-align: center !important;
}
</style>



// WEBPACK FOOTER //
// src/modules/campaign-manage/components/analytics/JourneyActions.vue